import React, { Component } from 'react';
import { connect } from 'react-redux';
import styles from './Counters.module.scss';
import InactivityManager from 'utils/InactivityManager';
import DragDrop from 'components/Draggable/DragDrop';
import FlippingComponent from 'components/FlippingComponent/FlippingComponent';
import { countersFlipCard } from 'store/actions/activity';
import PropTypes from 'prop-types';
import { random } from 'lodash';
import { t } from '../translations';

/* Error logging */
const ERRORS = {
  ERROR_TYPES: {
    TooFew: 'TooFew',
    TooMany: 'TooMany',
    MissedTarget: 'MissedTarget'
  },
  COMPETENCY: {
    Counting: 'Counting',
    DragAndDrop: 'DragAndDrop'
  }
};

const TIMEOUT = process.env.REACT_APP_INACTIVITY_TIMEOUT;

const REASON = {
  didNotFlipCard: 'Did not flip card',
  didNotTouchCube: 'Did not touch cube'
};

class Counters extends Component {
  constructor(props) {
    super(props);

    const availableCounters =
      3 === props.level
        ? props.currentRound.data.counters + random(2, 5)
        : props.currentRound.data.counters;

    this.inactivityManager = new InactivityManager(
      TIMEOUT,
      this.playInstructions,
      REASON.didNotFlipCard
    );

    this.state = {
      /* automatic = colored squares should move automatically to fill the required number */
      automatic: props.level === 1,
      availableCounters,
      counter: 0,
      flipped: false,
      inProgress: false,
      done: false,
      togglePosition: new Array(availableCounters).fill(false),
      targetList: []
    };
  }

  playInstructions = () => {
    const {
      props: {
        currentRound: { audio, data }
      }
    } = this;

    if (data.instructions.beforeFlip) {
      const sound = audio[data.instructions.beforeFlip.src];
      sound && sound.play();
    }
  };

  playKeepGoing = () => {
    const { data, audio } = this.props.currentRound;
    const sound = audio[data.instructions.keepGoing.src];
    sound && sound.play();
  };

  componentDidMount() {
    const targetList = [];
    const {
      props: {
        currentRound: { data }
      },
      state: { availableCounters }
    } = this;

    for (let i = 0; i < availableCounters; i++) {
      targetList.push({
        ref: React.createRef(),
        isCorrect: i < data.counters
      });
    }

    this.setState({
      targetList
    });

    setTimeout(() => {
      this.playInstructions();
      this.inactivityManager.watch();
    }, 500);
  }

  componentDidUpdate(prevProps) {
    const {
      state: { done },
      props: {
        forwardButton: { pulseTimeStamp },
        level
      }
    } = this;

    const {
      forwardButton: { pulseTimeStamp: prevStamp }
    } = prevProps;

    if (pulseTimeStamp && pulseTimeStamp !== prevStamp && 3 === level && !done) {
      this.logError(ERRORS.ERROR_TYPES.TooFew);
      this.playKeepGoing();
    }
  }

  handleFlip = () => {
    const {
      state: { done },
      props: {
        level,
        currentRound: { audio, data, stopAllSounds }
      }
    } = this;

    stopAllSounds();

    this.inactivityManager.clear();

    if (level > 1) {
      this.inactivityManager = new InactivityManager(
        TIMEOUT,
        this.playKeepGoing,
        REASON.didNotTouchCube
      );
      this.inactivityManager.watch();
    }

    if (data.instructions.afterFlip && !done) {
      const sound = audio[data.instructions.afterFlip.src];
      sound && sound.play();
    }

    this.setState({ flipped: true });
    this.props.countersFlipCard(true);
  };

  automaticMode = () => {
    const {
      state: { togglePosition },
      props: {
        currentRound: { audio, data },
        onRoundEnd
      }
    } = this;
    let {
      state: { counter }
    } = this;

    counter++;

    if (typeof data.numbers[counter] !== 'undefined') {
      const sound = audio[data.numbers[counter].src];
      sound && sound.play();
    }
    if (counter === data.counters) {
      this.setState({ done: true, counter });
      onRoundEnd();
    } else {
      togglePosition[counter] = true;
      this.setState({
        inProgress: false,
        togglePosition,
        counter
      });
    }
  };

  onTapStart = () => {
    const {
      state: { done },
      props: {
        commonSounds,
        flashForwardButton,
        currentRound: { audio, data }
      }
    } = this;

    if (done) {
      this.logError(ERRORS.ERROR_TYPES.TooMany);
      flashForwardButton();
      const { wrong: sndWrong } = commonSounds.common;

      const sound = audio[data.instructions.tooMany.src];
      sndWrong && sndWrong.play();
      sndWrong && sndWrong.once('end', sound.play());
      return;
    }

    this.setState({ inProgress: true });
  };

  onSnap = correct => {
    this.inactivityManager.clear();

    const {
      state: { counter, flipped },
      props: {
        currentRound: { audio, data },
        level,
        onRoundEnd,
        flashForwardButton
      }
    } = this;

    if (false === correct) {
      this.logError(ERRORS.ERROR_TYPES.TooMany);
      flashForwardButton();
      const sound = audio[data.instructions.tooMany.src];
      sound && sound.play();
      return;
    }

    if (typeof data.numbers[counter + 1] !== 'undefined') {
      const sound = audio[data.numbers[counter + 1]];
      sound && sound.play();
    }

    if (counter + 1 === data.counters || (3 === level && flipped && counter + 1 > 0)) {
      const isDone = counter + 1 === data.counters;
      onRoundEnd(isDone); // called to show the forward button; isDone will allow advancing to next activity
    }

    this.setState({
      inProgress: false,
      counter: counter + 1,
      done: counter + 1 === data.counters
    });

    process.nextTick(() => {
      if (3 === level && counter + 1 === data.counters) flashForwardButton();
    });
  };

  onBadDrag = (x, y) => {
    this.logError(ERRORS.ERROR_TYPES.MissedTarget, { x, y });
  };

  logError = (errorType, { ...coords } = false) => {
    const {
      props: {
        currentRound: {
          data: { counters }
        },
        logError
      },
      state: { counter }
    } = this;
    const timeStamp = new Date().toISOString();

    const error = {};
    switch (errorType) {
      case ERRORS.ERROR_TYPES.TooMany:
        error.error = ERRORS.ERROR_TYPES.TooMany;
        error.competency = ERRORS.COMPETENCY.Counting;
        error.actual = { counters: counters + 1 };
        error.expected = { counters: counters };
        error.timestamp = timeStamp;
        logError(error);
        break;

      case ERRORS.ERROR_TYPES.TooFew:
        error.error = ERRORS.ERROR_TYPES.TooFew;
        error.competency = ERRORS.COMPETENCY.Counting;
        error.actual = { counters: counter };
        error.expected = { counters: counters };
        error.timestamp = timeStamp;
        logError(error);
        break;

      case ERRORS.ERROR_TYPES.MissedTarget:
        error.error = ERRORS.ERROR_TYPES.MissedTarget;
        error.competency = ERRORS.COMPETENCY.DragAndDrop;
        error.actual = { x: coords.x, y: coords.y };
        error.expected = { target: 'FlipCard' };
        error.timestamp = timeStamp;
        logError(error);
        break;
    }
  };

  componentWillUnmount() {
    this.inactivityManager.clear();
    this.props.currentRound.stopAllSounds();
    this.props.countersFlipCard(false);
  }

  render() {
    const {
      props: {
        currentRound: { data },
        level
      },
      state: {
        automatic,
        availableCounters,
        counter,
        done,
        flipped,
        inProgress,
        targetList,
        togglePosition
      },
      automaticMode,
      onBadDrag,
      onTapStart,
      onSnap
    } = this;

    const sceneRef = React.createRef();
    const classes = [styles.Squares];
    flipped && classes.push(styles.Flipped);
    const activityDone = done && 3 !== level;
    const cardDescriptions = {
      cardFrontDescription: data.cardFront.description,
      cardBackDescription: `${t('FlippingBackOfCardShowing')} ${counter ?? 0} ${t(
        'FlippingCounter'
      )}`
    };

    return (
      targetList.length > 0 && (
        <div className={classes.join(' ')}>
          <div className={styles.Header}>
            <div className={styles.SquaresContainer} id="counters-square">
              {[...Array(availableCounters)].map((val, idx) => {
                /* Is this the topmost card in the stack? */
                const isTopCard = idx === counter;
                const allowDrag = !automatic && !activityDone && !inProgress && isTopCard;
                const allowTap =
                  (automatic && !inProgress) || (!activityDone && !inProgress && isTopCard);

                // pieces are not allowed to have order (tabIndex) === 0
                // only the first box should be focusable and clickable
                const order = idx === counter ? 1 : -1;

                return (
                  <DragDrop.DraggableWithKeyboard
                    classList={{ initial: 'Counters' }}
                    activityType={'Counters'}
                    allowDrag={flipped && allowDrag}
                    allowTap={flipped && allowTap}
                    animationTime={automatic ? 1000 : 500}
                    divId={`square-${idx}`}
                    key={`square-${idx}`}
                    level={level}
                    onBadDrag={onBadDrag}
                    onTap={() => onTapStart()}
                    onSnap={automatic ? automaticMode : onSnap}
                    targets={[targetList[idx]]}
                    targetWrapper={sceneRef}
                    togglePosition={togglePosition[idx]}
                    distance={20}
                    order={order}
                    label={`${t('CountersCube')} ${idx + 1}`}
                    labelForMovedPiece={`${t('MovedCountersCube')} ${idx + 1}`}
                    mainContainerId={'activity-container'}
                  >
                    <span style={{ backgroundColor: data.counterColor }} />
                  </DragDrop.DraggableWithKeyboard>
                );
              })}
            </div>
          </div>
          <FlippingComponent
            onCardTurned={() => this.handleFlip()}
            counter={counter}
            faceFront={
              <img
                src={data.cardFront.src}
                width={360}
                height={540}
                id="card-face"
                data-tester-counters={data.counters}
              />
            }
            faceBack={
              <div className={styles.Scene} ref={sceneRef} data-counters={counter}>
                {[...Array(availableCounters)].map((val, idx) => (
                  <DragDrop.Target
                    forwardedRef={targetList[idx].ref}
                    divId={`targetForCube${idx}`}
                    key={`square-${idx}`}
                    outline={idx < data.counters && 2 === level && data.counterColor}
                    activityType="Counters"
                  />
                ))}
              </div>
            }
            cardDescriptions={cardDescriptions}
          />
          <div className={styles.MiniCard}>
            <img src={data.cardFront.src} alt={data.cardFront.description} />
          </div>
        </div>
      )
    );
  }
}

Counters.propTypes = {
  commonSounds: PropTypes.object,
  currentRound: PropTypes.object,
  level: PropTypes.number,
  logError: PropTypes.func,
  onRoundEnd: PropTypes.func,
  flashForwardButton: PropTypes.func,
  forwardButton: PropTypes.shape({
    pulseTimeStamp: PropTypes.number
  }),
  countersFlipCard: PropTypes.func
};

export default connect(null, { countersFlipCard })(Counters);
